import React, { useState, useEffect } from "react";
import {
  Container,
  Stack,
  Title,
  Text,
  Button,
  Box,
  Paper,
  Group,
  TextInput,
  CopyButton,
  ActionIcon,
  Tooltip,
} from "@mantine/core";
import {
  IconArrowsExchange,
  IconCurrencyBitcoin,
  IconCopy,
  IconCheck,
} from "@tabler/icons-react";
import SatoshiConverterBackground from "../satoshiConverter/components/SatoshiConverterBackground";
import RizfulPitch from "../rizfulPitch/RizfulPitch";
import ServerRenderedBlock, {
  hasServerRenderedBlock,
} from "../helpers/ServerRenderedBlock";
import ToolLogo from "../helpers/ToolLogo";

const SATS_PER_BTC = 100_000_000;

const SatsToBtcContainer: React.FC = () => {
  const [satsInput, setSatsInput] = useState("");
  const [btcResult, setBtcResult] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const satsParam = params.get("sats");
    if (satsParam && /^\d+$/.test(satsParam)) {
      setSatsInput(satsParam);
    }
  }, []);

  useEffect(() => {
    if (!satsInput) {
      setBtcResult("");
      return;
    }
    const sats = parseInt(satsInput, 10);
    if (isNaN(sats)) {
      setBtcResult("");
      return;
    }
    const btc = sats / SATS_PER_BTC;
    setBtcResult(btc.toFixed(8).replace(/0+$/, "").replace(/\.$/, ""));

    const base = window.location.origin + window.location.pathname;
    window.history.replaceState(null, "", `${base}?sats=${satsInput}`);
  }, [satsInput]);

  const handleSatsChange = (value: string) => {
    const cleaned = value.replace(/[^0-9]/g, "");
    setSatsInput(cleaned);
  };

  const formattedSats = satsInput
    ? parseInt(satsInput, 10).toLocaleString()
    : "0";

  return (
    <SatoshiConverterBackground>
      <Container size={700} px="md" py="xl">
        <Stack gap="lg">
          <ToolLogo />

          {/* H1 + live line come from Rails (dynamic/_btc_sats_ssr) */}
          {hasServerRenderedBlock("ssr-tool-answer") ? (
            <ServerRenderedBlock id="ssr-tool-answer" />
          ) : (
            <Box ta="center">
              <Title order={1} c="white" mb="xs">
                Convert Satoshis to BTC
              </Title>
              <Text c="dimmed" size="sm">
                1 Bitcoin = 100,000,000 satoshis. Enter a sats amount to see the
                BTC equivalent.
              </Text>
            </Box>
          )}

          <Paper
            p="xl"
            radius="md"
            style={{
              backgroundColor: "rgba(0, 0, 0, 0.45)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(10px)",
            }}
          >
            <Stack gap="lg">
              <Box>
                <Text size="sm" c="dimmed" mb={4} fw={500}>
                  Satoshis (sats)
                </Text>
                <TextInput
                  placeholder="Enter amount in satoshis"
                  value={satsInput}
                  onChange={(e) => handleSatsChange(e.currentTarget.value)}
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  size="lg"
                  styles={{
                    input: {
                      fontFamily: "courier-prime, monospace",
                      fontSize: "20px",
                      backgroundColor: "rgba(0, 0, 0, 0.3)",
                      border: "1px solid rgba(255, 255, 255, 0.15)",
                      color: "white",
                      textAlign: "right",
                    },
                  }}
                  rightSection={
                    <Text size="sm" c="dimmed" mr="xs">
                      sats
                    </Text>
                  }
                  rightSectionWidth={50}
                />
              </Box>

              <Group justify="center">
                <IconArrowsExchange
                  size={28}
                  color="rgba(255,255,255,0.4)"
                  style={{ transform: "rotate(90deg)" }}
                />
              </Group>

              {/* Result */}
              {btcResult && parseFloat(btcResult) > 0 ? (
                <Paper
                  p="md"
                  radius="sm"
                  style={{
                    backgroundColor: "rgba(255, 165, 0, 0.08)",
                    border: "1px solid rgba(255, 165, 0, 0.2)",
                  }}
                >
                  <CopyButton value={btcResult}>
                    {({ copied, copy }) => (
                      <Tooltip
                        label={copied ? "Copied!" : "Copy to clipboard"}
                        withArrow
                        position="top"
                      >
                        <Box
                          onClick={copy}
                          style={{
                            cursor: "pointer",
                            borderRadius: "8px",
                            padding: "8px 12px",
                            transition: "background-color 150ms ease",
                            backgroundColor: copied
                              ? "rgba(32, 201, 151, 0.1)"
                              : "transparent",
                          }}
                        >
                          <Group justify="center" gap={6} align="baseline">
                            <Text
                              size="xs"
                              fw={400}
                              style={{
                                fontFamily: "courier-prime, monospace",
                                color: "rgba(255, 255, 255, 0.35)",
                              }}
                            >
                              {formattedSats} sats =
                            </Text>
                            <Text
                              size="xl"
                              fw={700}
                              style={{
                                fontFamily: "courier-prime, monospace",
                                color: copied ? "#20c997" : "#f7931a",
                                fontSize: "24px",
                                transition: "color 150ms ease",
                              }}
                            >
                              {btcResult} BTC
                            </Text>
                            <ActionIcon
                              variant={copied ? "filled" : "subtle"}
                              color={copied ? "teal" : "orange"}
                              size="lg"
                              radius="xl"
                              style={{ transition: "all 150ms ease" }}
                            >
                              {copied ? (
                                <IconCheck size={18} />
                              ) : (
                                <IconCopy size={18} />
                              )}
                            </ActionIcon>
                          </Group>
                        </Box>
                      </Tooltip>
                    )}
                  </CopyButton>
                </Paper>
              ) : (
                <Paper
                  p="md"
                  radius="sm"
                  style={{
                    backgroundColor: "rgba(255, 165, 0, 0.08)",
                    border: "1px solid rgba(255, 165, 0, 0.2)",
                  }}
                >
                  <Group justify="center" gap="sm">
                    <IconCurrencyBitcoin size={22} color="#f7931a" />
                    <Text
                      size="lg"
                      fw={700}
                      c="dimmed"
                      style={{ fontFamily: "courier-prime, monospace" }}
                    >
                      0 BTC
                    </Text>
                  </Group>
                </Paper>
              )}
            </Stack>
          </Paper>

          {/* Quick reference (server-rendered with live USD when present) */}
          {hasServerRenderedBlock("ssr-tool-table") ? (
            <ServerRenderedBlock id="ssr-tool-table" />
          ) : (
            <Paper
              p="md"
              radius="md"
              style={{
                backgroundColor: "rgba(0, 0, 0, 0.3)",
                border: "1px solid rgba(255, 255, 255, 0.08)",
              }}
            >
              <Text size="sm" fw={600} c="white" mb="xs">
                Quick Reference
              </Text>
              {[
                ["100,000,000 sats", "1 BTC"],
                ["10,000,000 sats", "0.1 BTC"],
                ["1,000,000 sats", "0.01 BTC"],
                ["100,000 sats", "0.001 BTC"],
                ["10,000 sats", "0.0001 BTC"],
                ["1,000 sats", "0.00001 BTC"],
                ["100 sats", "0.000001 BTC"],
                ["1 sat", "0.00000001 BTC"],
              ].map(([sats, btc]) => (
                <Group
                  key={sats}
                  justify="space-between"
                  py={4}
                  style={{
                    borderBottom: "1px solid rgba(255,255,255,0.05)",
                  }}
                >
                  <Text
                    size="xs"
                    c="dimmed"
                    style={{ fontFamily: "courier-prime, monospace" }}
                  >
                    {sats}
                  </Text>
                  <Text
                    size="xs"
                    c="orange"
                    style={{ fontFamily: "courier-prime, monospace" }}
                  >
                    {btc}
                  </Text>
                </Group>
              ))}
            </Paper>
          )}

          {/* Links to other tools */}
          <Group justify="center" gap="sm">
            <Button
              component="a"
              href="/btc-to-sats-converter"
              variant="subtle"
              color="orange"
              size="xs"
            >
              Convert BTC → Sats
            </Button>
            <Button
              component="a"
              href="/satoshis-to-usd-converter"
              variant="subtle"
              color="gray"
              size="xs"
            >
              Sats to USD
            </Button>
          </Group>


          <RizfulPitch
            lead="Ready to receive your first sats? Any Lightning wallet can pay a Rizful Lightning Address."
          />
          <ServerRenderedBlock id="ssr-tool-footer" />
        </Stack>
      </Container>
    </SatoshiConverterBackground>
  );
};

export default SatsToBtcContainer;
